import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { testResultService } from '../services/testResultService';
import { TestResult, StudentPerformanceAnalytics } from '../types/testResult';

import { SubjectPerformanceChart } from '../components/SubjectPerformanceChart';
import { MonthlyProgressChart } from '../components/MonthlyProgressChart';
import { DifficultyAnalysisChart } from '../components/DifficultyAnalysisChart';
import { TestResultCard } from '../components/TestResultCard';

/**
 * Test Results Page - Shows student's test results and analytics
 */
export const TestResultsPage: React.FC = () => {
  const navigate = useNavigate();
  const [results, setResults] = useState<TestResult[]>([]);
  const [analytics, setAnalytics] = useState<StudentPerformanceAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'results' | 'analytics'>('overview');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load both results and analytics in parallel
      const [resultsData, analyticsData] = await Promise.all([
        testResultService.getStudentResults(),
        testResultService.getPerformanceAnalytics()
      ]);

      // Sort results by most recent (fallback to startedAt if completedAt missing)
      const sortedResults = resultsData.sort((a, b) => {
        const dateA = new Date(a.completedAt || a.startedAt).getTime();
        const dateB = new Date(b.completedAt || b.startedAt).getTime();
        return dateB - dateA;
      });

      setResults(sortedResults);
      setAnalytics(analyticsData);
    } catch (err) {
      console.error('Error loading test results:', err);
      setError('Failed to load test results. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleViewResult = (result: TestResult) => {
    // If test is in progress, navigate to resume it
    if (result.status === 'IN_PROGRESS') {
      navigate(`/test/${result.testId}`);
    } else {
      // Otherwise view the result details
      navigate(`/results/${result.id}`);
    }
  };

  const recentCompletedResults = results
    .filter((result) => result.status === 'COMPLETED' || result.status === 'SUBMITTED')
    .slice(0, 5);

  const averageRecentPercentage = recentCompletedResults.length > 0
    ? recentCompletedResults.reduce((sum, result) => sum + result.percentage, 0) / recentCompletedResults.length
    : 0;

  const bestPercentage = analytics && analytics.averageScore > 0
    ? (analytics.bestScore / analytics.averageScore) * analytics.averagePercentage
    : 0;

  const averageMinutesPerTest = analytics && analytics.totalTests > 0
    ? Math.round((analytics.totalTimeSpent || 0) / analytics.totalTests)
    : 0;

  const subjectPerformanceList = analytics?.subjectWisePerformance
    ? Object.values(analytics.subjectWisePerformance).sort((a, b) => b.averagePercentage - a.averagePercentage)
    : [];

  const difficultyPerformanceList = analytics?.difficultyWisePerformance
    ? Object.values(analytics.difficultyWisePerformance)
    : [];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0456FF] mx-auto"></div>
          <p className="mt-4 text-[#0456FF]">Loading your test results...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-xl mb-4">⚠️</div>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={loadData}
            className="px-4 py-2 bg-[#0456FF] text-white rounded-md hover:bg-[#0456FF]/90"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <h1 className="text-3xl font-bold text-[#0456FF]">Test Results</h1>
            <p className="mt-2 text-gray-600">
              View your test performance and analytics
            </p>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            {[
              { key: 'overview', label: 'Overview', icon: '📊' },
              { key: 'results', label: 'Test Results', icon: '📝' },
              { key: 'analytics', label: 'Analytics', icon: '📈' }
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key as any)}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.key
                    ? 'border-[#0456FF] text-[#0456FF]'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <span className="mr-2">{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'overview' && analytics && (
          <div className="space-y-8">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white p-6 rounded-md border border-blue-200">
                <div className="flex items-center">
                  <div className="p-2 bg-blue-100 rounded-md">
                    <span className="text-2xl">📝</span>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Total Tests</p>
                    <p className="text-2xl font-bold text-[#0456FF]">{analytics.totalTests}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-md border border-blue-200">
                <div className="flex items-center">
                  <div className="p-2 bg-green-100 rounded-md">
                    <span className="text-2xl">📊</span>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Average Percentage</p>
                    <p className="text-2xl font-bold text-[#0456FF]">
                      {analytics.averagePercentage.toFixed(1)}%
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-md border border-blue-200">
                <div className="flex items-center">
                  <div className="p-2 bg-yellow-100 rounded-md">
                    <span className="text-2xl">🏆</span>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Best Percentage</p>
                    <p className="text-2xl font-bold text-[#0456FF]">
                      {bestPercentage.toFixed(1)}%
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-md border border-blue-200">
                <div className="flex items-center">
                  <div className="p-2 bg-purple-100 rounded-md">
                    <span className="text-2xl">⏱️</span>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Time Spent</p>
                    <p className="text-2xl font-bold text-[#0456FF]">
                      {analytics.totalTimeSpent && analytics.totalTimeSpent > 0
                        ? Math.round(analytics.totalTimeSpent / 60)
                        : 0}h
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="bg-white p-6 rounded-md border border-blue-200">
                <h3 className="text-lg font-semibold text-[#0456FF] mb-4">Monthly Progress</h3>
                <MonthlyProgressChart data={analytics?.monthlyProgress || []} />
              </div>

              <div className="bg-white p-6 rounded-md border border-blue-200">
                <h3 className="text-lg font-semibold text-[#0456FF] mb-4">Subject Performance</h3>
                <SubjectPerformanceChart data={analytics?.subjectWisePerformance ? Object.values(analytics.subjectWisePerformance) : []} />
              </div>
            </div>

            {/* Recent Results */}
            <div className="bg-white rounded-md border border-blue-200">
              <div className="p-6 border-b border-blue-200">
                <h3 className="text-lg font-semibold text-[#0456FF]">Recent Test Results</h3>
              </div>
              <div className="p-6">
                {analytics?.recentResults && analytics.recentResults.length > 0 ? (
                  <div className="space-y-4">
                    {analytics.recentResults.map((result) => (
                      <TestResultCard
                        key={result.id}
                        result={result}
                        onView={() => handleViewResult(result)}
                      />
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-8">No test results available</p>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'results' && (
          <div className="bg-white rounded-md border border-blue-200">
            <div className="p-6 border-b border-blue-200">
              <h3 className="text-lg font-semibold text-[#0456FF]">All Test Results</h3>
            </div>
            <div className="p-6">
              {results.length > 0 ? (
                <div className="space-y-4">
                  {results.map((result) => (
                    <TestResultCard
                      key={result.id}
                      result={result}
                      onView={() => handleViewResult(result)}
                      detailed={true}
                    />
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">No test results available</p>
              )}
            </div>
          </div>
        )}

        {activeTab === 'analytics' && (
          <div className="space-y-8">
            {!analytics ? (
              <div className="bg-white p-8 rounded-md border border-blue-200 text-center">
                <p className="text-gray-600">No analytics data available yet.</p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div className="bg-white p-6 rounded-md border border-blue-200">
                    <p className="text-sm text-gray-600">Best Percentage</p>
                    <p className="text-2xl font-bold text-[#0456FF]">{bestPercentage.toFixed(1)}%</p>
                  </div>
                  <div className="bg-white p-6 rounded-md border border-blue-200">
                    <p className="text-sm text-gray-600">Recent Form (Last 5)</p>
                    <p className="text-2xl font-bold text-[#0456FF]">{averageRecentPercentage.toFixed(1)}%</p>
                  </div>
                  <div className="bg-white p-6 rounded-md border border-blue-200">
                    <p className="text-sm text-gray-600">Avg Time / Test</p>
                    <p className="text-2xl font-bold text-[#0456FF]">{averageMinutesPerTest} min</p>
                  </div>
                  <div className="bg-white p-6 rounded-md border border-blue-200">
                    <p className="text-sm text-gray-600">Tracked Subjects</p>
                    <p className="text-2xl font-bold text-[#0456FF]">{subjectPerformanceList.length}</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div className="bg-white p-6 rounded-md border border-blue-200">
                    <h3 className="text-lg font-semibold text-[#0456FF] mb-4">Difficulty Analysis</h3>
                    <DifficultyAnalysisChart data={difficultyPerformanceList} />
                  </div>
                  <div className="bg-white p-6 rounded-md border border-blue-200">
                    <h3 className="text-lg font-semibold text-[#0456FF] mb-4">Progress Trend</h3>
                    <MonthlyProgressChart data={analytics.monthlyProgress || []} />
                  </div>
                </div>

                <div className="bg-white rounded-md border border-blue-200">
                  <div className="p-6 border-b border-blue-200">
                    <h3 className="text-lg font-semibold text-[#0456FF]">Subject Deep Dive</h3>
                    <p className="text-sm text-gray-600 mt-1">Performance and accuracy by subject</p>
                  </div>
                  <div className="p-6 overflow-x-auto">
                    {subjectPerformanceList.length > 0 ? (
                      <table className="min-w-full text-sm">
                        <thead>
                          <tr className="border-b text-left text-gray-600">
                            <th className="py-2 pr-4">Subject</th>
                            <th className="py-2 pr-4">Tests</th>
                            <th className="py-2 pr-4">Questions</th>
                            <th className="py-2 pr-4">Correct</th>
                            <th className="py-2 pr-4">Wrong</th>
                            <th className="py-2 pr-4">Accuracy</th>
                          </tr>
                        </thead>
                        <tbody>
                          {subjectPerformanceList.map((subject) => (
                            <tr key={subject.subject} className="border-b last:border-b-0">
                              <td className="py-3 pr-4 font-medium text-gray-900">{subject.subject}</td>
                              <td className="py-3 pr-4 text-gray-700">{subject.totalTests}</td>
                              <td className="py-3 pr-4 text-gray-700">{subject.totalQuestions}</td>
                              <td className="py-3 pr-4 text-green-700 font-medium">{subject.totalCorrect}</td>
                              <td className="py-3 pr-4 text-red-700 font-medium">{subject.totalWrong}</td>
                              <td className="py-3 pr-4">
                                <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                                  subject.averagePercentage >= 75
                                    ? 'bg-green-100 text-green-800'
                                    : subject.averagePercentage >= 50
                                      ? 'bg-yellow-100 text-yellow-800'
                                      : 'bg-red-100 text-red-800'
                                }`}>
                                  {subject.averagePercentage.toFixed(1)}%
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <p className="text-gray-500 text-center py-4">No subject-level data available</p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div className="bg-white p-6 rounded-md border border-blue-200">
                    <h3 className="text-lg font-semibold text-[#0456FF] mb-4">Subject Performance Overview</h3>
                    <SubjectPerformanceChart data={subjectPerformanceList} />
                  </div>
                  <div className="bg-white p-6 rounded-md border border-blue-200">
                    <h3 className="text-lg font-semibold text-[#0456FF] mb-4">Insights</h3>
                    <div className="space-y-3 text-sm">
                      <div className="p-3 bg-blue-50 rounded border border-blue-100">
                        <span className="font-semibold text-blue-900">Strongest Subject: </span>
                        <span className="text-blue-800">
                          {subjectPerformanceList[0]?.subject || 'N/A'}
                          {subjectPerformanceList[0] ? ` (${subjectPerformanceList[0].averagePercentage.toFixed(1)}%)` : ''}
                        </span>
                      </div>
                      <div className="p-3 bg-amber-50 rounded border border-amber-100">
                        <span className="font-semibold text-amber-900">Needs Focus: </span>
                        <span className="text-amber-800">
                          {subjectPerformanceList.length > 0
                            ? `${subjectPerformanceList[subjectPerformanceList.length - 1].subject} (${subjectPerformanceList[subjectPerformanceList.length - 1].averagePercentage.toFixed(1)}%)`
                            : 'N/A'}
                        </span>
                      </div>
                      <div className="p-3 bg-slate-50 rounded border border-slate-200">
                        <span className="font-semibold text-slate-900">Suggestion: </span>
                        <span className="text-slate-700">
                          Prioritize timed practice in your weaker subject and review mistakes from the latest 5 tests.
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
